import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  CalendarClock,
  Timer,
  ShieldCheck,
  Wifi,
  Cpu,
  KeyRound,
  Copy,
  Replace,
  Server,
  ClipboardCheck,
  TriangleAlert,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const PAGE_TITLE = 'Oracle Forms 12c to 14c Upgrade — The Platform Upgrade Path | EZ Cloud';
const PAGE_DESCRIPTION =
  'Oracle Forms 14c (14.1.2) was released February 2025. For the large installed base of Oracle Forms 12c customers facing the December 2026 support deadline, the fastest, lowest-risk path is the Forms 12c to 14c platform upgrade — keep your Forms applications, upgrade the Fusion Middleware platform they run on. In-place and side-by-side patterns, new 14c features, and how the alternatives (APEX, ADF) compare.';
const CANONICAL = 'https://ezcloud.ai/solutions/oracle-forms-12c-to-14c-upgrade';

const schemaAbout = [
  'Oracle Forms',
  'Oracle Forms 14c',
  'Oracle Forms 12c',
  'Oracle Reports',
  'Oracle Fusion Middleware',
  'Oracle Forms Upgrade',
];

const KEY_DATES = [
  {
    date: 'December 2026',
    label: 'Fusion Middleware 12c Premier Support ends',
    detail:
      'Oracle Fusion Middleware 12c (12.2.1.4) — the platform Oracle Forms 12c runs on — reaches the end of Premier Support on the normal Lifetime Support schedule. This is the date most Forms 12c customers are planning their upgrade window around.',
    severity: 'amber',
  },
  {
    date: 'December 2027',
    label: 'Fusion Middleware 12c Extended Support ends',
    detail:
      'Per Oracle\'s Lifetime Support Policy, Extended Support for Fusion Middleware 12c (12.2.1.4) runs through December 2027. Extended Support provides critical patches and security updates, after which Sustaining Support applies.',
    severity: 'red',
  },
  {
    date: 'February 2025',
    label: 'Oracle Forms & Reports 14c (14.1.2) released',
    detail:
      'Oracle Forms & Reports 14c (14.1.2) shipped in February 2025 with HTTP/2, TLS 1.3, Java Virtual Threads, and no database credentials required at deployment. Upgrading your Forms applications to run on the 14c platform buys approximately 8 years of supported runway.',
    severity: 'green',
  },
];

const WHATS_NEW = [
  {
    icon: Wifi,
    title: 'HTTP/2 support',
    detail:
      'Oracle Forms 14c (14.1.2) supports HTTP/2, bringing multiplexed connections and modern transport performance to the Forms runtime tier your existing applications already run on.',
  },
  {
    icon: ShieldCheck,
    title: 'TLS 1.3',
    detail:
      'The 14c release supports TLS 1.3, aligning the Forms tier with current transport-security expectations for enterprise and public-sector deployments — often a driver for the upgrade in its own right.',
  },
  {
    icon: Cpu,
    title: 'Java Virtual Threads',
    detail:
      'Forms 14c runs on a Java runtime with Virtual Threads, improving concurrency handling for the middle tier without changes to your Forms application logic.',
  },
  {
    icon: KeyRound,
    title: 'No DB credentials at deployment',
    detail:
      'In 14c, database credentials are no longer required at deployment time — a meaningful operational and security simplification for teams managing Forms deployments across environments.',
  },
];

const UPGRADE_STEPS = [
  {
    step: '1',
    title: 'Confirm your 12c starting point (12.2.1.4)',
    body:
      'Upgrades to Forms & Reports 14c (14.1.2) are supported from 12.2.1.4 (or 12.2.1.19). Implementations on older Fusion Middleware versions must first move to 12.2.1.4 before the 14c upgrade. Step one is confirming exactly which 12c version each Forms environment runs today. This is the same starting-point check that governs the parallel Oracle WebLogic 14c upgrade underneath.',
  },
  {
    step: '2',
    title: 'Choose in-place or side-by-side',
    body:
      'An in-place upgrade reuses the same Oracle Home — it is faster but is not reversible without a full backup. A side-by-side upgrade builds a new Oracle Home while the existing 12c installation keeps running, which lowers risk and gives you a clean fallback. Side-by-side is the pattern most enterprise Forms shops choose. Either way this is a full platform upgrade, not a patch set.',
  },
  {
    step: '3',
    title: 'Build the 14c Fusion Middleware domain',
    body:
      'EZ Cloud stands up the new 14c Fusion Middleware domain on WebLogic Server 14c using the same out-of-place domain upgrade discipline applied to WebCenter and SOA Suite. This is the platform your Forms applications will run on — the Forms applications themselves are preserved.',
  },
  {
    step: '4',
    title: 'Recompile and redeploy the Forms modules',
    body:
      'Your existing Forms and Reports modules are recompiled against 14c and redeployed into the new domain. Because this is a platform upgrade rather than a rewrite, the application logic — your fmb/mmb/pll libraries, triggers, and Reports — carries forward. New 14c behaviours such as no-DB-credentials-at-deployment are picked up at this stage.',
  },
  {
    step: '5',
    title: 'Validate against 14c',
    body:
      'Every Forms module, menu, library, and Report is validated in the 14c environment — HTTP/2 and TLS 1.3 configuration, single sign-on integration, printing, and any EBS or database integrations. Because side-by-side leaves 12c running, validation happens without disrupting the live system.',
  },
  {
    step: '6',
    title: 'Cutover',
    body:
      'Once validation is complete, traffic is cut over to the 14c platform. With the side-by-side pattern the 12c environment remains available as a fallback until the 14c deployment is proven in production.',
  },
];

const OPTIONS = [
  {
    icon: Replace,
    tag: 'Option 1 · Recommended first step',
    title: 'Forms 12c → 14c platform upgrade',
    body:
      'Keep your Forms applications and upgrade the Fusion Middleware platform they run on. This is the fastest, lowest-risk path off the December 2026 deadline — it preserves your application investment and buys approximately 8 years of supported runway, which defers the larger rewrite decision rather than forcing it now. For most Forms shops, this is what to do first. EZ Cloud delivers this work founder-led.',
    highlight: true,
  },
  {
    icon: ArrowRight,
    tag: 'Option 2 · Rewrite',
    title: 'Migrate to Oracle APEX',
    body:
      'Rebuilding Forms applications in Oracle APEX is a legitimate long-term direction, but it is a larger, different kind of project — a rewrite, not an upgrade. It makes sense when the goal is a browser-native, low-code future rather than continuity of the existing application. Upgrading to 14c now and rewriting later are not mutually exclusive: the 14c platform upgrade buys the runway to plan an APEX move deliberately instead of under deadline pressure.',
  },
  {
    icon: ArrowRight,
    tag: 'Option 3 · Rewrite',
    title: 'Migrate to Oracle ADF',
    body:
      'Rebuilding in Oracle ADF is another forward path, and ADF itself has a 14c (14.1.2) upgrade path. Like APEX, an ADF migration is a rewrite with its own scope, cost, and timeline — different from the platform upgrade. The 14c platform upgrade keeps that option open on your own timeline rather than forcing the rewrite decision under the December 2026 deadline.',
  },
  {
    icon: ArrowRight,
    tag: 'Option 4 · Exit',
    title: 'Exit Oracle Forms entirely',
    body:
      'Some organisations use the December 2026 window as the moment to move off Oracle Forms to a different platform altogether. This is the largest change of the four and the one least driven by the support deadline itself. The 14c platform upgrade is often the pragmatic move that creates room to plan an exit deliberately rather than under time pressure.',
  },
];

const FAQS = [
  {
    q: 'Is Oracle Forms dead?',
    a: 'No. Oracle Forms is actively supported and Oracle released Oracle Forms & Reports 14c (14.1.2) in February 2025. Fusion Middleware 12c reaches the end of Premier Support in December 2026 on the normal Lifetime Support schedule, and the 14c release is the forward path that keeps your Forms applications running with support through the next window.',
  },
  {
    q: 'Is there an Oracle Forms 14c?',
    a: 'Yes. Oracle Forms & Reports 14c is release 14.1.2 and it was released in February 2025. It adds HTTP/2, TLS 1.3, Java Virtual Threads, and removes the requirement for database credentials at deployment. Upgrading your Forms applications to run on 14c is a full platform upgrade, not a patch set.',
  },
  {
    q: 'Should we upgrade Forms to 14c or migrate to APEX?',
    a: 'For most Forms 12c customers, the 12c → 14c platform upgrade is the right first step: it is faster and lower-risk, keeps your applications intact, and buys approximately 8 years of supported runway. A migration to Oracle APEX is a rewrite — a larger, different project that makes sense when you specifically want a low-code, browser-native rebuild. The 14c upgrade defers the rewrite decision rather than forcing it under the December 2026 deadline, so you can plan a move to APEX on your own timeline if and when it is right for you.',
  },
  {
    q: 'How long does a Forms 12c to 14c upgrade take?',
    a: 'Timelines depend on the number of Forms environments, the volume of Forms and Reports modules, and integration surface (single sign-on, printing, EBS or database connections). Because the 14c move is a platform upgrade that preserves the applications rather than a rewrite, it is materially shorter than an APEX or ADF migration. The side-by-side pattern lets validation run against 14c while 12c stays live, so cutover happens on your schedule.',
  },
  {
    q: 'What is the difference between in-place and side-by-side?',
    a: 'An in-place upgrade reuses the same Oracle Home — it is faster but is not reversible without a full backup. A side-by-side upgrade builds a new Oracle Home while the existing 12c installation keeps running, which lowers risk and provides a clean fallback path. Most enterprise Forms shops choose side-by-side for exactly that reason. Both are full platform upgrades supported from 12.2.1.4.',
  },
  {
    q: 'What about Oracle Reports?',
    a: 'Oracle Reports is part of the same Oracle Forms & Reports 14c (14.1.2) release from February 2025, so your Reports move forward together with your Forms on the platform upgrade. Reports modules are validated and redeployed alongside the Forms modules in the new 14c domain. This keeps the Forms-and-Reports estate on a single supported platform.',
  },
  {
    q: 'Do we have to rewrite our Forms applications?',
    a: 'No. The 12c → 14c platform upgrade specifically avoids a rewrite — you keep your Forms applications and upgrade the Fusion Middleware platform they run on. Your fmb/mmb/pll libraries, triggers, menus, and Reports carry forward; they are recompiled against 14c and redeployed. A rewrite to APEX or ADF is a separate, optional decision that the 14c upgrade lets you defer.',
  },
  {
    q: 'Is there a WebLogic dependency in the 14c upgrade?',
    a: 'Yes. Oracle Forms 14c runs on Oracle WebLogic Server 14c, so the Forms platform upgrade includes standing up the 14c Fusion Middleware domain on WebLogic 14c. EZ Cloud performs this as an out-of-place domain build — the same discipline used for the WebLogic, WebCenter, and SOA Suite 14c upgrades. See our Oracle WebLogic 14c upgrade page for the platform layer underneath.',
  },
];

const OracleForms12cTo14cUpgrade = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-07-06',
    dateModified: '2026-07-06',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': CANONICAL,
    },
    about: schemaAbout.map((name) => ({ '@type': 'Thing', name })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        canonical={CANONICAL}
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/oracle-fusion-middleware-12c-end-of-support' },
          {
            name: 'Oracle Forms 12c to 14c Upgrade',
            url: CANONICAL,
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-07-06" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Timer className="w-4 h-4" aria-hidden />
              Oracle Forms 14c · Platform upgrade · December 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle Forms 12c to 14c upgrade
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Keep your Forms applications. Upgrade the platform they run on.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Oracle Forms 14c (14.1.2) shipped February 2025. Keep your Forms applications, upgrade
              the platform they run on — the fastest, lowest-risk path off the December 2026 support
              deadline.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('forms-14c-hero')}
              >
                <CalendarClock className="mr-2 w-5 h-5" aria-hidden />
                Book a Forms Upgrade Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/oracle-fusion-middleware-12c-end-of-support">
                  See the full Fusion Middleware 12c guide
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR — direct answer for AI Overviews */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              The short answer
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              The Oracle Forms 12c to 14c upgrade, in plain terms
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle Forms &amp; Reports 14c (release 14.1.2) shipped in February 2025.
                </strong>{' '}
                For the large installed base of Oracle Forms 12c customers, the 12c → 14c platform
                upgrade is the fastest, lowest-risk path off the December 2026 support deadline: you
                keep your Forms applications and upgrade the Fusion Middleware platform they run on.
                It is a full platform upgrade, not a patch set.
              </p>
              <p>
                Oracle{' '}
                <Link
                  to="/solutions/oracle-fusion-middleware-12c-end-of-support"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  Fusion Middleware 12c (12.2.1.4)
                </Link>{' '}
                reaches the end of Premier Support in December 2026 and Extended Support in December
                2027, on the normal Lifetime Support schedule. Upgrades to 14c (14.1.2) are supported
                from 12.2.1.4 (or 12.2.1.19); older versions move to 12.2.1.4 first. The upgrade can
                run in-place (same Oracle Home, faster) or side-by-side (new Oracle Home, lower risk,
                12c stays running).
              </p>
              <p>
                Forms 14c adds{' '}
                <strong className="text-gray-900">
                  HTTP/2, TLS 1.3, Java Virtual Threads, and removes the requirement for database
                  credentials at deployment
                </strong>
                . It runs on{' '}
                <Link
                  to="/solutions/oracle-weblogic-14c-upgrade"
                  className="text-primary font-semibold hover:text-accent transition-colors"
                >
                  Oracle WebLogic Server 14c
                </Link>{' '}
                underneath. Migrating to Oracle APEX or ADF is a separate, larger rewrite — the 14c
                platform upgrade buys roughly 8 years of runway and lets you defer that decision.
              </p>
            </div>
          </div>
        </section>

        {/* Key dates */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The timeline
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Key dates for the Forms 12c decision
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Per Oracle's Lifetime Support Policy. The 12c support window and the 14c release
              together frame the decision for every Oracle Forms customer in 2026.
            </p>
            <div className="space-y-5">
              {KEY_DATES.map((d) => (
                <Card
                  key={d.date}
                  className={`p-7 bg-white border shadow-sm ${
                    d.severity === 'red'
                      ? 'border-red-200'
                      : d.severity === 'amber'
                        ? 'border-amber-200'
                        : 'border-green-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide ${
                        d.severity === 'red'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : d.severity === 'amber'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                      }`}
                    >
                      {d.date}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{d.label}</h3>
                      <p className="text-gray-700 leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's new in Forms 14c */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What's new in Forms 14c
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four meaningful additions in Forms 14c (14.1.2)
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The February 2025 release brings modern transport, concurrency, and deployment changes
              to the Forms runtime tier — without touching your Forms application logic.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {WHATS_NEW.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* The platform upgrade path */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The platform upgrade path
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              In-place vs side-by-side — a full platform upgrade
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
              The Forms 12c → 14c move is the same out-of-place Fusion Middleware domain upgrade EZ
              Cloud performs for{' '}
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="text-primary font-semibold hover:text-accent transition-colors"
              >
                WebCenter
              </Link>{' '}
              and SOA Suite, with{' '}
              <Link
                to="/solutions/oracle-weblogic-14c-upgrade"
                className="text-primary font-semibold hover:text-accent transition-colors"
              >
                WebLogic 14c
              </Link>{' '}
              underneath. It is supported from 12.2.1.4 and is a full upgrade, not a patch set.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Copy className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">In-place</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Reuses the same Oracle Home. Faster to execute, but not reversible without a full
                  backup — so a solid backup is essential before starting. A good fit for
                  lower-complexity environments where a controlled maintenance window is available.
                </p>
              </Card>
              <Card className="p-7 bg-white border border-accent/40 shadow-sm ring-1 ring-accent/20">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Replace className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Side-by-side</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Builds a new Oracle Home while the existing 12c installation keeps running. Lower
                  risk, with a clean fallback and validation against 14c without disrupting the live
                  system. This is the pattern most enterprise Forms shops choose.
                </p>
              </Card>
            </div>

            <div className="space-y-5">
              {UPGRADE_STEPS.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Upgrade vs migrate — your options honestly */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Your options, honestly
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Upgrade vs migrate — four forward paths
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              All four are legitimate. The platform upgrade is the fastest, lowest-risk option and is
              what most Forms shops need first; APEX and ADF rewrites are a larger, different kind of
              project. The 14c upgrade buys roughly 8 years of runway and defers the rewrite
              decision.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {OPTIONS.map(({ icon: Icon, tag, title, body, highlight }) => (
                <Card
                  key={title}
                  className={`p-7 bg-white border shadow-sm ${
                    highlight ? 'border-accent/40 ring-1 ring-accent/20' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-accent/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <p className="text-xs font-bold uppercase tracking-wide text-accent">{tag}</p>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
                  <p className="text-gray-700 leading-relaxed">{body}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How EZ Cloud delivers the 14c upgrade */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud delivers it
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Founder-led delivery of the Forms 14c upgrade
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              EZ Cloud delivers the Forms 12c → 14c platform upgrade directly, founder-led, using the
              same out-of-place Fusion Middleware domain discipline applied to WebCenter, SOA Suite,
              and WebLogic.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                  <ClipboardCheck className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Read the existing Forms and FMW estate
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Andrew reads your running Forms and Reports estate and its Fusion Middleware
                  configuration directly — confirming the 12.2.1.4 starting point, cataloguing
                  modules, libraries, single sign-on, printing, and EBS or database integrations.
                </p>
              </Card>
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                  <Server className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Out-of-place 14c domain build
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  The new 14c Fusion Middleware domain is built on WebLogic Server 14c side-by-side
                  with your running 12c environment, giving a clean, low-risk target with a fallback
                  path throughout.
                </p>
              </Card>
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                  <Copy className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Recompile and redeploy Forms modules
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Your Forms and Reports modules are recompiled against 14c and redeployed into the
                  new domain, preserving application logic and picking up 14c behaviours such as
                  no-DB-credentials-at-deployment.
                </p>
              </Card>
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Validate, then cut over</h3>
                <p className="text-gray-700 leading-relaxed">
                  Every module, menu, Report, and integration is validated on 14c — including HTTP/2
                  and TLS 1.3 — before cutover. With side-by-side, 12c remains available as a
                  fallback until the 14c deployment is proven in production.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Deprecation / compatibility watch */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 bg-amber-50 border border-amber-200 shadow-sm">
              <div className="flex items-start gap-4">
                <TriangleAlert className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" aria-hidden />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2">
                    Compatibility watch
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Confirm the starting point and the supporting stack
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    A few things are worth confirming before an upgrade project starts, so the plan
                    reflects your actual environment:
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <ClipboardCheck className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      Upgrades to Forms 14c (14.1.2) are supported from 12.2.1.4 (or 12.2.1.19) —
                      older Fusion Middleware versions move to 12.2.1.4 first.
                    </li>
                    <li className="flex items-start gap-2">
                      <ClipboardCheck className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      In-place upgrades are irreversible without a full backup — take the backup
                      before starting, or choose side-by-side.
                    </li>
                    <li className="flex items-start gap-2">
                      <ClipboardCheck className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden />
                      Forms 14c runs on WebLogic Server 14c — confirm the supporting{' '}
                      <Link
                        to="/solutions/oracle-weblogic-14c-upgrade"
                        className="text-primary font-semibold hover:text-accent transition-colors"
                      >
                        WebLogic 14c
                      </Link>{' '}
                      platform is part of the plan.
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle Fusion Middleware December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the forward paths off Fusion Middleware 12c — including the
              Forms 14c platform upgrade — with stack-specific considerations and diagnostic
              questions.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the Forms 12c → 14c upgrade
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your Forms 12c → 14c upgrade
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year Oracle
              specialist with 20+ years across Oracle Forms, Reports, WebLogic, and Fusion
              Middleware. Walk through your Forms estate and the 14c platform upgrade in the context
              of your specific environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('forms-14c-cta')}
              >
                <CalendarClock className="mr-2 w-5 h-5" aria-hidden />
                Book a Forms Upgrade Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/oracle-fusion-middleware-12c-end-of-support">
                  See the full Fusion Middleware 12c guide
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OracleForms12cTo14cUpgrade;
